import React from "react";
import { Box, Typography } from "@mui/material";
import { ImageMetadata } from "../types";

interface AverageFocusQualityCardProps {
  imagesForView: ImageMetadata[];
}

const AverageFocusQualityCard: React.FC<AverageFocusQualityCardProps> = ({
  imagesForView,
}) => {
  // Only use light frames for statistics
  const lightFrames = imagesForView.filter((img) => img.FrameType === "LIGHT");

  // Helper to check for valid numbers
  const isValidNumber = (val: any) => typeof val === "number" && isFinite(val);

  // Filter valid frames for each metric
  const validHFR = lightFrames.filter((img) => isValidNumber(img.HFR));
  const validStars = lightFrames.filter((img) =>
    isValidNumber(img.DetectedStars)
  );
  const validFWHM = lightFrames.filter((img) => isValidNumber(img.FWHM));
  const validEcc = lightFrames.filter((img) => isValidNumber(img.Eccentricity));

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 3,
        p: 3,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        minHeight: 220,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "text.primary", mb: 2 }}
      >
        <span style={{ marginRight: 8 }}>🔬</span> Average Focus Quality
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
          gap: 2,
          flex: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            HFR (Half Flux Radius)
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: "text.primary", fontWeight: 700 }}
          >
            {(
              validHFR.reduce((sum, img) => sum + img.HFR, 0) /
              (validHFR.length || 1)
            ).toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Lower is better - measures star size
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Detected Stars
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: "text.primary", fontWeight: 700 }}
          >
            {Math.round(
              validStars.reduce((sum, img) => sum + img.DetectedStars, 0) /
                (validStars.length || 1)
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Average per frame
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            FWHM
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: "text.primary", fontWeight: 700 }}
          >
            {(
              validFWHM.reduce((sum, img) => sum + img.FWHM, 0) /
              (validFWHM.length || 1)
            ).toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Full width at half maximum
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Eccentricity
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: "text.primary", fontWeight: 700 }}
          >
            {(
              validEcc.reduce((sum, img) => sum + img.Eccentricity, 0) /
              (validEcc.length || 1)
            ).toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Star shape (0=circle)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AverageFocusQualityCard;
